import type { Game } from '../../game'
import type { TrackedGame, TrackedGameReview } from '../types/TrackedGame'

// Sentinel review used when the backend returned no review at all for a free LE game.
// `published: true` keeps the LiveOps Summary button enabled for users who have the
// LE role but no explicit tracking permission. The empty `content` map is intentional:
// the modal itself will show "no data" if the backend does not subsequently return
// review content for the user, which is the actual BE-side gap to address separately.
const FREE_LIVE_EVENTS_REVIEW_STUB: TrackedGameReview = {
  analystId: '',
  published: true,
  content: {},
}

/**
 * Make sure the resolved `TrackedGame` for a free Live Events game exposes a published
 * review so the LiveOps Summary button on the Live Events Tracker calendar sidebar is
 * enabled. The backend may return Homescapes-style free games under
 * `gamesWithoutPermission` with either no `review` at all or `review.published === false`
 * for users who do not have explicit tracking permission, which (correctly) disables the
 * button via `LiveEventsOverviewButton`'s `trackedGame.review && (trackedGame.review.published || internalUser)`
 * gate. For free LE games every authenticated LE-role user is allowed to view the
 * analyst overview, so we promote the review to published here.
 *
 * Non-free games and free games that already have a published review are returned
 * unchanged so analyst-authored content is preserved verbatim. (GR-2889)
 */
export const withFreeLiveEventsReviewStub = (trackedGame: TrackedGame, freeLiveEventsGames: Pick<Game, 'id'>[] | undefined): TrackedGame => {
  const isFree = freeLiveEventsGames?.some((g) => g.id === trackedGame.game.id) ?? false
  if (!isFree) return trackedGame
  if (trackedGame.review?.published) return trackedGame

  return {
    ...trackedGame,
    review: trackedGame.review ? { ...trackedGame.review, published: true } : FREE_LIVE_EVENTS_REVIEW_STUB,
  }
}
